import get from 'lodash/get';
import store from '../..';
import { GenerateContentValidationStateSegment } from '../../stateSegments/GenerateContentValidationStateSegment';
import {
  fetchGeneratedContentValidation,
  fetchGeneratedContentValidationStatus,
  fetchGeneratedContentValidationResults,
} from '../../../../services/Content';
import { popoverStateSegment } from '../../stateSegments/popoverStateSegment';
import {
  contentListStateSegment,
  selectedChannel,
} from '../../stateSegments/contentListSegment';
import {
  ArrowPosition,
  PopoverVariant,
} from '../../../../components/atoms/Popover';
import evaluateQualityChecks, {
  getNeedsReviewCount,
} from '../../../../utils/evaluateQualityChecks';
import { QcReviewName } from '../../../../types/QualityChecks';
import {
  ApplicableRecommendation,
  ApplicableRecommendationData,
  QualityCheck,
  ValidationStatusApiResponse,
  GeneratedContentValidationNew,
} from '../../../../types/contentValidation';
import { translate } from '../../../../localization';
import {
  trackSuggestedRecommendationsByCategory,
  trackSuggestedRecommendationsBySeverity,
  trackSuggestedRecommendationsByChannel,
  trackSuggestedRecommendationsUserNtid,
  trackAudienceFitByTargetAudience,
  trackAudienceFitBySegment,
  trackAudienceFitScore,
  trackAudienceFitBrand,
  trackAudienceFitCountry,
  trackAudienceFitIndication,
  trackAudienceFitUserNtid,
} from '../../../../services/Analytics';
import { getEncodedUserNtid, getMappedName } from '../../helpers/common';
import { contentParamsSegment } from '../../stateSegments/paramsSegment';
import { PAGE_ROUTES } from '../../../../constants/common';
import { featureFlagsStateSegment } from '../../../globalStore/stateSegments/featureFlagsStateSegment';
import { qualityValidationStateSegment } from '../../stateSegments/qualityValidationStateSegment';

// Helper function to check if user is currently on generate-content route and has content
const isOnGenerateContentRoute = (): boolean => {
  if (globalThis.window !== undefined) {
    const isCorrectRoute =
      globalThis.window.location.pathname === PAGE_ROUTES.GENERATE_CONTENT;
    const { contentList } = store.get(contentListStateSegment);
    const hasContent = contentList && contentList.length > 0;
    return isCorrectRoute && hasContent;
  }
  return false;
};

export const getGeneratedContentValidationsWithoutPolling = async ({
  contentId,
  sessionId,
  contentGroupId,
  action,
}: {
  contentId: string;
  sessionId: string;
  contentGroupId: string;
  action: string;
}) => {
  const featureFlags = store.get(featureFlagsStateSegment);
  const isContentRuleCenterEnabled = get(
    featureFlags,
    'features.content-rules',
    false
  );
  store.set(GenerateContentValidationStateSegment, prev => {
    return {
      ...prev,
      [contentId]: {
        ...prev[contentId],
        isLoading: true,
      },
    };
  });

  const { data, success } = await fetchGeneratedContentValidation({
    contentId,
    sessionId,
    contentGroupId,
    action,
  });

  if (data && success) {
    const currentContentValidation = get(
      data,
      'generatedContentValidation[0]',
      { qualityChecks: [], applicableRecommendations: [] }
    );
    const qualityChecks = get(
      currentContentValidation,
      'qualityChecks',
      []
    ) as QualityCheck[];
    const applicableRecommendations: ApplicableRecommendationData[] = (
      get(
        currentContentValidation,
        'applicableRecommendations',
        []
      ) as ApplicableRecommendation[]
    ).map((el: ApplicableRecommendation) => ({
      ...el,
      isSelected: false,
    }));

    const qualityChecksStatus = evaluateQualityChecks(qualityChecks);
    const needsReviewCount = getNeedsReviewCount(qualityChecks);

    store.set(GenerateContentValidationStateSegment, prev => {
      return {
        ...prev,
        [contentId]: {
          ...prev[contentId],
          error: false,
          errorMessage: '',
          isLoading: false,
          status: qualityChecksStatus,
          needsReviewCount,
          qualityChecks,
          applicableRecommendations,
          retryParams: {
            contentId,
            sessionId,
            contentGroupId,
            action,
          },
          retryCount: 0,
        },
      };
    });

    const getPopoverDetails = (status: QcReviewName) => {
      if (status === QcReviewName.PASSED) {
        return {
          description: 'Click here to see the Quality Checks.',
          title: 'Content Looks Great!',
          variant: PopoverVariant.SUCCESS,
        };
      }

      return {
        description: 'Click here to see the Quality Checks.',
        title: 'Your Content Needs Review',
      };
    };

    const popoverDetails = getPopoverDetails(qualityChecksStatus);

    // Only show popover if user is still on generate-content route
    if (!isContentRuleCenterEnabled && isOnGenerateContentRoute()) {
      store.set(popoverStateSegment, prev => {
        return {
          ...prev,
          showPopover: true,
          variant: popoverDetails.variant || PopoverVariant.DEFAULT,
          arrowPosition: ArrowPosition.LEFT_CENTER,
          title: popoverDetails.title,
          description: popoverDetails.description,
          showCloseButton: true,
          showIcon: true,
          buttonLabel: '',
          qualityChecksStatus,
        };
      });
    }
  } else {
    store.set(GenerateContentValidationStateSegment, prev => {
      const contentIdData = prev[contentId] || {}; // Safely access existing data

      return {
        ...prev,
        [contentId]: {
          ...contentIdData, // Spread existing contentId data
          error: true,
          errorMessage: 'Failed to load quality checks',
          isLoading: false,
          needsReviewCount: 0,
          qualityChecks: [],
          applicableRecommendations: [],
          retryParams: {
            contentId,
            sessionId,
            contentGroupId,
            action,
          },
          retryCount: (contentIdData.retryCount || 0) + 1, // Increment retryCount, defaulting to 0 if it doesn't exist
        },
      };
    });

    const validationData = store.get(GenerateContentValidationStateSegment);
    const selectedVaildationData = validationData[contentId] || {};

    if (selectedVaildationData.retryCount > 3) {
      // Only show popover if user is still on generate-content route
      if (isOnGenerateContentRoute()) {
        store.set(popoverStateSegment, prev => {
          return {
            ...prev,
            showPopover: true,
            variant: PopoverVariant.WARNING,
            arrowPosition: ArrowPosition.LEFT_CENTER,
            title: translate('quality_checks_failed_to_load'),
            description: translate(
              'quality_checks_failed_to_load_description_server_down'
            ),
            buttonLabel: '',
            onButtonClick: () => {
              getGeneratedContentValidationsWithoutPolling({
                contentId,
                sessionId,
                contentGroupId,
                action,
              });
              store.set(popoverStateSegment, prevPropover => {
                return {
                  ...prevPropover,
                  showPopover: false,
                };
              });
            },
            showCloseButton: true,
            showIcon: true,
            qualityChecksStatus: QcReviewName.FAILED,
          };
        });
      }
    } else if (isOnGenerateContentRoute()) {
      store.set(popoverStateSegment, prev => {
        return {
          ...prev,
          showPopover: true,
          variant: PopoverVariant.WARNING,
          arrowPosition: ArrowPosition.LEFT_CENTER,
          title: translate('quality_checks_failed_to_load'),
          description: translate('quality_checks_failed_to_load_description'),
          buttonLabel: translate('common_retry'),
          onButtonClick: () => {
            getGeneratedContentValidationsWithoutPolling({
              contentId,
              sessionId,
              contentGroupId,
              action,
            });
            store.set(popoverStateSegment, prevPropover => {
              return {
                ...prevPropover,
                showPopover: false,
              };
            });
          },
          showCloseButton: true,
          showIcon: true,
          qualityChecksStatus: QcReviewName.FAILED,
        };
      });
    }
  }
};

// Helper function to poll validation status
const getGeneratedContentValidationStatusPoller = async ({
  contentId,
  sessionId,
  contentGroupId,
  action,
  validationId: _validationId,
}: {
  contentId: string;
  sessionId: string;
  contentGroupId: string;
  action: string;
  validationId?: string;
}): Promise<boolean> => {
  const maxAttempts = 30; // Max 30 attempts
  const pollInterval = 2000; // Poll every 2 seconds
  let attempts = 0;

  return new Promise(resolve => {
    const poll = async () => {
      try {
        attempts++;

        const response = await fetchGeneratedContentValidationStatus({
          contentId,
          sessionId,
          contentGroupId,
          action,
        });

        if (response.success && response.data) {
          const statusData = response as ValidationStatusApiResponse;
          const validationStatus = get(
            statusData,
            'data.generatedContentValidationStatus[0].status',
            'IN_PROGRESS'
          );

          // Update polling state
          store.set(qualityValidationStateSegment, prev => ({
            ...prev,
            [contentId]: {
              ...prev[contentId],
              isPolling: true,
            },
          }));

          if (validationStatus === 'COMPLETED') {
            // Update polling state to false
            store.set(qualityValidationStateSegment, prev => ({
              ...prev,
              [contentId]: {
                ...prev[contentId],
                isPolling: false,
              },
            }));
            resolve(true);
            return;
          }

          if (attempts >= maxAttempts) {
            // Timeout - stop polling
            store.set(qualityValidationStateSegment, prev => ({
              ...prev,
              [contentId]: {
                ...prev[contentId],
                isPolling: false,
                error: true,
                errorMessage: 'Validation timeout',
              },
            }));
            resolve(false);
            return;
          }

          // Continue polling
          setTimeout(poll, pollInterval);
        } else {
          resolve(false);
        }
      } catch {
        // Handle error and stop polling
        resolve(false);
      }
    };

    poll();
  });
};

// Helper function to get validation results
const getGeneratedContentValidationResults = async ({
  contentId,
  sessionId,
  contentGroupId,
  action,
}: {
  contentId: string;
  sessionId: string;
  contentGroupId: string;
  action: string;
}) => {
  try {
    const response = await fetchGeneratedContentValidationResults({
      contentId,
      sessionId,
      contentGroupId,
      action,
    });

    if (response.success && response.data) {
      const validationData = get(
        response.data,
        'generatedContentValidation',
        null
      ) as GeneratedContentValidationNew | null;

      // Check if it's the new format
      if (validationData && 'overallContentQuality' in validationData) {
        // New API format - store the full validation data
        const overallStatus = get(
          validationData,
          'overallContentQuality.status',
          'PASSED'
        );

        // Determine quality checks status based on overall status
        const getQualityChecksStatus = (status: string): QcReviewName => {
          if (status === 'HEALTHY') return QcReviewName.PASSED;
          if (status === 'NEEDS_IMPROVEMENT' || status === 'AT_RISK')
            return QcReviewName.NEEDS_REVIEW;
          return QcReviewName.PASSED;
        };

        const qualityChecksStatus = getQualityChecksStatus(overallStatus);

        const totalRecommendations = get(
          validationData,
          'totals.recommendations.total',
          0
        );

        // Initialize selected recommendation IDs with:
        // 1. All audience fit recommendations (should be selected by default)
        // 2. All BLOCKER severity content rules recommendations (should be selected by default)
        const getInitialSelectedRecommendationIds = (): Set<string> => {
          const selectedIds = new Set<string>();

          // Add all audience fit recommendations
          const audienceFitRecommendations =
            validationData?.audienceFitValidation?.recommendations || [];
          audienceFitRecommendations.forEach(rec => {
            selectedIds.add(rec.recommendationId);
          });

          // Add all BLOCKER severity content rules recommendations
          const contentRulesValidation =
            validationData?.contentRulesValidation || [];
          contentRulesValidation.forEach(rule => {
            if (rule.severity.type === 'BLOCKER') {
              const recommendations = rule.validation.recommendations || [];
              recommendations.forEach(rec => {
                selectedIds.add(rec.recommendationId);
              });
            }
          });

          return selectedIds;
        };

        // Store the results in the quality validation state segment
        store.set(qualityValidationStateSegment, prev => {
          const existingState = prev[contentId] || {};
          // Only initialize selected IDs if they don't already exist (preserve user's selections)
          const selectedRecommendationIds =
            existingState.selectedRecommendationIds ??
            getInitialSelectedRecommendationIds();

          return {
            ...prev,
            [contentId]: {
              ...existingState,
              error: false,
              errorMessage: '',
              isLoading: false,
              isPolling: false,
              status: qualityChecksStatus,
              needsReviewCount: totalRecommendations,
              qualityChecks: [], // Legacy format
              applicableRecommendations: [], // Legacy format
              validationData, // New format data
              selectedRecommendationIds,
              retryParams: {
                contentId,
                sessionId,
                contentGroupId,
                action,
              },
              retryCount: 0,
            },
          };
        });

        const userEncodedNtid = getEncodedUserNtid();

        // Track suggested recommendations analytics
        const contentRulesValidation =
          validationData?.contentRulesValidation || [];

        // Calculate category-wise recommendation counts
        const categoryCounts: Record<string, number> = {};
        contentRulesValidation.forEach(rule => {
          const categoryName = rule.category.name;
          const recommendationCount =
            rule.validation.recommendations?.length || 0;
          if (recommendationCount > 0) {
            categoryCounts[categoryName] =
              (categoryCounts[categoryName] || 0) + recommendationCount;
          }
        });

        // Calculate severity-wise recommendation counts
        // Map severity names to analytics tracking labels
        const severityNameMapping: Record<string, string> = {
          Blocker: 'Must Apply',
          Warning: 'Should Apply',
          Info: 'Good To Have',
        };
        const severityCounts: Record<string, number> = {};
        contentRulesValidation.forEach(rule => {
          const severityName =
            severityNameMapping[rule.severity.name] || rule.severity.name;
          const recommendationCount =
            rule.validation.recommendations?.length || 0;
          if (recommendationCount > 0) {
            severityCounts[severityName] =
              (severityCounts[severityName] || 0) + recommendationCount;
          }
        });

        // Track category-wise counts
        if (Object.keys(categoryCounts).length > 0) {
          trackSuggestedRecommendationsByCategory(categoryCounts);
        }

        // Track severity-wise counts
        if (Object.keys(severityCounts).length > 0) {
          trackSuggestedRecommendationsBySeverity(severityCounts);
        }

        // Track channel with total recommendations
        const currentChannel = store.get(selectedChannel);
        if (currentChannel && totalRecommendations > 0) {
          trackSuggestedRecommendationsByChannel(
            currentChannel,
            totalRecommendations
          );
        }

        // Track user encoded ntid
        trackSuggestedRecommendationsUserNtid(userEncodedNtid);

        // Track audience fit recommendations analytics
        const audienceFitValidation = validationData?.audienceFitValidation;
        const audienceFitRecommendationsCount =
          audienceFitValidation?.recommendations?.length || 0;

        if (audienceFitRecommendationsCount > 0) {
          // Get current params for brand, country, indication, audience, and segment
          const paramsUsed = store.get(contentParamsSegment);

          // Track selected target audience with audience fit recommendations count
          const selectedAudience = getMappedName(
            paramsUsed.currentAvailableAudienceOptions,
            paramsUsed.currentSelectedAudience
          );
          if (selectedAudience) {
            trackAudienceFitByTargetAudience(
              selectedAudience,
              audienceFitRecommendationsCount
            );
          }

          // Track selected segment with audience fit recommendations count
          const selectedSegment = getMappedName(
            paramsUsed.currentAvailableSegmentOptions,
            paramsUsed.currentSelectedSegment
          );
          if (selectedSegment) {
            trackAudienceFitBySegment(
              selectedSegment,
              audienceFitRecommendationsCount
            );
          }

          // Track audience fit score
          const audienceFitScore =
            audienceFitValidation?.confidenceLevel?.percentage || 0;
          trackAudienceFitScore(audienceFitScore);

          // Track selected brand
          const selectedBrand = getMappedName(
            paramsUsed.currentAvailableBrandOptions,
            paramsUsed.currentSelectedBrand
          );
          if (selectedBrand) {
            trackAudienceFitBrand(selectedBrand);
          }

          // Track selected country
          const selectedCountry = getMappedName(
            paramsUsed.currentAvailableCountryOptions,
            paramsUsed.currentSelectedCountry
          );
          if (selectedCountry) {
            trackAudienceFitCountry(selectedCountry);
          }

          // Track selected indication
          const selectedIndication = getMappedName(
            paramsUsed.currentAvailableIndicationOptions,
            paramsUsed.currentSelectedIndication
          );
          if (selectedIndication) {
            trackAudienceFitIndication(selectedIndication);
          }

          // Track user encoded ntid for audience fit
          trackAudienceFitUserNtid(userEncodedNtid);
        }

        return {
          success: true,
          qualityChecksStatus,
          needsReviewCount: totalRecommendations,
        };
      }

      // Legacy format fallback
      const currentContentValidation = get(
        response.data,
        'generatedContentValidation[0]',
        { qualityChecks: [], applicableRecommendations: [] }
      );
      const qualityChecks = get(
        currentContentValidation,
        'qualityChecks',
        []
      ) as QualityCheck[];
      const applicableRecommendations: ApplicableRecommendationData[] = (
        get(
          currentContentValidation,
          'applicableRecommendations',
          []
        ) as ApplicableRecommendation[]
      ).map((el: ApplicableRecommendation) => ({
        ...el,
        isSelected: false,
      }));

      const qualityChecksStatus = evaluateQualityChecks(qualityChecks);
      const needsReviewCount = getNeedsReviewCount(qualityChecks);

      // Store the results in the quality validation state segment
      store.set(qualityValidationStateSegment, prev => {
        const existingState = prev[contentId] || {};
        return {
          ...prev,
          [contentId]: {
            ...existingState,
            error: false,
            errorMessage: '',
            isLoading: false,
            isPolling: false,
            status: qualityChecksStatus,
            needsReviewCount,
            qualityChecks,
            applicableRecommendations,
            selectedRecommendationIds:
              existingState.selectedRecommendationIds ?? new Set<string>(),
            retryParams: {
              contentId,
              sessionId,
              contentGroupId,
              action,
            },
            retryCount: 0,
          },
        };
      });

      return { success: true, qualityChecksStatus, needsReviewCount };
    }

    return { success: false };
  } catch {
    // Handle error by updating state
    store.set(qualityValidationStateSegment, prev => {
      const existingState = prev[contentId] || {};
      return {
        ...prev,
        [contentId]: {
          ...existingState,
          error: true,
          errorMessage: 'Failed to load quality checks results',
          isLoading: false,
          isPolling: false,
        },
      };
    });
    return { success: false };
  }
};

// Update the main function to use content rule center flow when enabled
export const getGeneratedContentValidationsWithPolling = async ({
  contentId,
  sessionId,
  contentGroupId,
  action,
}: {
  contentId: string;
  sessionId: string;
  contentGroupId: string;
  action: string;
}) => {
  const featureFlags = store.get(featureFlagsStateSegment);
  const isContentRuleCenterEnabled = get(
    featureFlags,
    'features.content-rules',
    false
  );

  if (!isContentRuleCenterEnabled) {
    // Use the old flow
    return getGeneratedContentValidationsWithoutPolling({
      contentId,
      sessionId,
      contentGroupId,
      action,
    });
  }

  // New flow with polling
  store.set(qualityValidationStateSegment, prev => {
    const existingState = prev[contentId] || {};
    return {
      ...prev,
      [contentId]: {
        ...existingState,
        isLoading: true,
        isPolling: false,
        error: false,
        errorMessage: '',
        qualityChecks: [],
        applicableRecommendations: [],
        needsReviewCount: 0,
        // Don't initialize selectedRecommendationIds here - let it be set when validation data arrives
        // This ensures the default selection logic in getGeneratedContentValidationResults() works correctly
        selectedRecommendationIds: existingState.selectedRecommendationIds,
        retryParams: {
          contentId,
          sessionId,
          contentGroupId,
          action,
        },
        retryCount: 0,
      },
    };
  });

  // Step 1: POST to initiate validation
  const { data, success } = await fetchGeneratedContentValidation({
    contentId,
    sessionId,
    contentGroupId,
    action,
  });

  if (data && success) {
    const validationId = get(
      data,
      'generatedContentValidation[0].id',
      undefined
    );

    // Store validation ID
    store.set(qualityValidationStateSegment, prev => ({
      ...prev,
      [contentId]: {
        ...prev[contentId],
        validationId,
      },
    }));

    // Step 2: Start polling for status
    const pollingSuccess = await getGeneratedContentValidationStatusPoller({
      contentId,
      sessionId,
      contentGroupId,
      action,
      validationId,
    });

    if (pollingSuccess) {
      // Step 3: Fetch final results
      await getGeneratedContentValidationResults({
        contentId,
        sessionId,
        contentGroupId,
        action,
      });
    } else {
      // Polling failed or timed out
      store.set(qualityValidationStateSegment, prev => ({
        ...prev,
        [contentId]: {
          ...prev[contentId],
          error: true,
          errorMessage: 'Validation polling failed or timed out',
          isLoading: false,
          isPolling: false,
        },
      }));
    }
  } else {
    // POST failed
    store.set(qualityValidationStateSegment, prev => ({
      ...prev,
      [contentId]: {
        ...prev[contentId],
        error: true,
        errorMessage: 'Failed to initiate validation',
        isLoading: false,
        isPolling: false,
      },
    }));
  }
};

// Main entry point that routes to the appropriate function based on feature flag
export const getGeneratedContentValidations = async ({
  contentId,
  sessionId,
  contentGroupId,
  action,
}: {
  contentId: string;
  sessionId: string;
  contentGroupId: string;
  action: string;
}) => {
  const featureFlags = store.get(featureFlagsStateSegment);
  const isContentRuleCenterEnabled = get(
    featureFlags,
    'features.content-rules',
    false
  );

  if (isContentRuleCenterEnabled) {
    return getGeneratedContentValidationsWithPolling({
      contentId,
      sessionId,
      contentGroupId,
      action,
    });
  } else {
    return getGeneratedContentValidationsWithoutPolling({
      contentId,
      sessionId,
      contentGroupId,
      action,
    });
  }
};
